import React, { useEffect, useState } from 'react';
import { Calculator, TrendingUp, Clock, DollarSign } from 'lucide-react';
const ROICalculator = () => {
  const [billCount, setBillCount] = useState(25);
  const [avgBillAmount, setAvgBillAmount] = useState(2000);
  const [processingTime, setProcessingTime] = useState(15);
  const [monthlySavings, setMonthlySavings] = useState(0);
  const [timeSaved, setTimeSaved] = useState(0);
  const [cashFlowBenefit, setCashFlowBenefit] = useState(0);
  // Calculate ROI whenever inputs change
  useEffect(() => {
    // Time savings calculation
    const newTimeSaved = billCount * processingTime / 60; // in hours
    setTimeSaved(newTimeSaved);
    // Cost savings calculation (assuming RM100/hour labor cost)
    const laborCost = 100;
    const laborSavings = newTimeSaved * laborCost;
    // Late fee avoidance (assuming 2% of bills have late fees averaging 5% of bill amount)
    const lateFeeAvoidance = billCount * 0.02 * avgBillAmount * 0.05;
    // Error reduction savings (assuming 1% error rate with recovery of 50% of bill amount)
    const errorSavings = billCount * 0.01 * avgBillAmount * 0.5;
    // Total monthly savings
    const newMonthlySavings = laborSavings + lateFeeAvoidance + errorSavings;
    setMonthlySavings(newMonthlySavings);
    // Cash flow benefit (30 days of float on all bills)
    const totalMonthlyBills = billCount * avgBillAmount;
    const annualInterestRate = 0.06; // 6% opportunity cost
    const newCashFlowBenefit = totalMonthlyBills * (annualInterestRate / 12);
    setCashFlowBenefit(newCashFlowBenefit);
  }, [billCount, avgBillAmount, processingTime]);
  return <section className="section bg-light-sky" data-id="element-247">
      <div className="container" data-id="element-248">
        <div className="text-center mb-16" data-id="element-249">
          <h2 className="font-outfit font-bold text-3xl md:text-4xl mb-6" data-id="element-250">
            Calculate Your Savings
          </h2>
          <p className="text-slate-grey text-lg max-w-3xl mx-auto" data-id="element-251">
            See how much time and money your business could save with Settl.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-252">
          <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-253">
            {/* Inputs */}
            <div className="p-8 border-b lg:border-b-0 lg:border-r border-gray-100" data-id="element-254">
              <h3 className="font-outfit font-semibold text-xl mb-6 flex items-center" data-id="element-255">
                <Calculator className="h-5 w-5 mr-2 text-settl-blue" data-id="element-256" />
                Your Business Details
              </h3>
              <div className="space-y-8" data-id="element-257">
                {/* Bill Count Slider */}
                <div data-id="element-258">
                  <div className="flex justify-between mb-2" data-id="element-259">
                    <label className="text-sm font-medium" data-id="element-260">Monthly Bills</label>
                    <span className="font-space-mono text-sm" data-id="element-261">{billCount}</span>
                  </div>
                  <input type="range" min="5" max="100" value={billCount} onChange={e => setBillCount(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-settl-blue" data-id="element-262" />
                  <div className="flex justify-between text-xs text-slate-grey mt-1" data-id="element-263">
                    <span data-id="element-264">5</span>
                    <span data-id="element-265">100+</span>
                  </div>
                </div>
                {/* Average Bill Amount Slider */}
                <div data-id="element-266">
                  <div className="flex justify-between mb-2" data-id="element-267">
                    <label className="text-sm font-medium" data-id="element-268">
                      Average Bill Amount (RM)
                    </label>
                    <span className="font-space-mono text-sm" data-id="element-269">
                      {avgBillAmount.toLocaleString()}
                    </span>
                  </div>
                  <input type="range" min="500" max="10000" step="500" value={avgBillAmount} onChange={e => setAvgBillAmount(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-settl-blue" data-id="element-270" />
                  <div className="flex justify-between text-xs text-slate-grey mt-1" data-id="element-271">
                    <span data-id="element-272">RM 500</span>
                    <span data-id="element-273">RM 10,000</span>
                  </div>
                </div>
                {/* Processing Time Slider */}
                <div data-id="element-274">
                  <div className="flex justify-between mb-2" data-id="element-275">
                    <label className="text-sm font-medium" data-id="element-276">
                      Minutes Per Bill (Processing)
                    </label>
                    <span className="font-space-mono text-sm" data-id="element-277">
                      {processingTime}
                    </span>
                  </div>
                  <input type="range" min="5" max="30" value={processingTime} onChange={e => setProcessingTime(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-settl-blue" data-id="element-278" />
                  <div className="flex justify-between text-xs text-slate-grey mt-1" data-id="element-279">
                    <span data-id="element-280">5 min</span>
                    <span data-id="element-281">30 min</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Results */}
            <div className="p-8 bg-gradient-to-br from-settl-blue/5 to-success-green/5" data-id="element-282">
              <h3 className="font-outfit font-semibold text-xl mb-6 flex items-center" data-id="element-283">
                <TrendingUp className="h-5 w-5 mr-2 text-success-green" data-id="element-284" />
                Your Potential Savings
              </h3>
              <div className="space-y-6" data-id="element-285">
                {/* Monthly Savings */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-success-green/20" data-id="element-286">
                  <div className="flex items-start" data-id="element-287">
                    <div className="bg-success-green/10 p-2 rounded-lg mr-4" data-id="element-288">
                      <DollarSign className="h-6 w-6 text-success-green" data-id="element-289" />
                    </div>
                    <div data-id="element-290">
                      <div className="text-sm text-slate-grey mb-1" data-id="element-291">
                        Total Monthly Savings
                      </div>
                      <div className="font-space-mono text-3xl font-bold text-success-green" data-id="element-292">
                        RM{' '}
                        {monthlySavings.toLocaleString(undefined, {
                        maximumFractionDigits: 0
                      })}
                      </div>
                      <div className="text-xs text-slate-grey mt-1" data-id="element-293">
                        RM{' '}
                        {(monthlySavings * 12).toLocaleString(undefined, {
                        maximumFractionDigits: 0
                      })}{' '}
                        annually
                      </div>
                    </div>
                  </div>
                </div>
                {/* Time Saved */}
                <div className="bg-white rounded-lg p-6 shadow-sm" data-id="element-294">
                  <div className="flex items-start" data-id="element-295">
                    <div className="bg-settl-blue/10 p-2 rounded-lg mr-4" data-id="element-296">
                      <Clock className="h-6 w-6 text-settl-blue" data-id="element-297" />
                    </div>
                    <div data-id="element-298">
                      <div className="text-sm text-slate-grey mb-1" data-id="element-299">
                        Monthly Time Saved
                      </div>
                      <div className="font-space-mono text-2xl font-bold" data-id="element-300">
                        {timeSaved.toFixed(1)} hours
                      </div>
                      <div className="text-xs text-slate-grey mt-1" data-id="element-301">
                        {(timeSaved * 12).toFixed(0)} hours annually
                      </div>
                    </div>
                  </div>
                </div>
                {/* Cash Flow Benefit */}
                <div className="bg-white rounded-lg p-6 shadow-sm" data-id="element-302">
                  <div className="flex items-start" data-id="element-303">
                    <div className="bg-settl-blue/10 p-2 rounded-lg mr-4" data-id="element-304">
                      <TrendingUp className="h-6 w-6 text-settl-blue" data-id="element-305" />
                    </div>
                    <div data-id="element-306">
                      <div className="text-sm text-slate-grey mb-1" data-id="element-307">
                        Cash Flow Benefit
                      </div>
                      <div className="font-space-mono text-2xl font-bold" data-id="element-308">
                        RM{' '}
                        {cashFlowBenefit.toLocaleString(undefined, {
                        maximumFractionDigits: 0
                      })}
                      </div>
                      <div className="text-xs text-slate-grey mt-1" data-id="element-309">
                        From 30-day payment terms
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-8" data-id="element-310">
                <a href="https://wa.me/60127743934/?text=I%20am%20interested%20in%20Settl" className="btn-primary w-full block text-center" data-id="element-311">
                  Start Saving Today
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default ROICalculator;